/*
 * The chat module's keyboard bindings (UX §1.6, X-A4 N16), registered into the one shortcut registry
 * through the `shortcuts` slot — which is what makes them appear in the keyboard sheet: an
 * undocumented shortcut cannot exist (A31).
 *
 * A binding is a definition, not a behaviour: the registry is built once per boot, while what a key
 * does depends on the conversation that is open. So a definition calls the *controller*, which the
 * open conversation installs for its lifetime and clears when it goes. With no conversation open the
 * conversation scopes are not on the stack either, and a key that reached here anyway does nothing
 * rather than reaching into a conversation that is no longer on screen.
 *
 * The six global ones are the exception, and deliberately so: moving to the next conversation,
 * browsing channels and writing to somebody are not things a conversation lends — they are wanted
 * from Activity, from Search and from a settings page just as much — so they read the model directly,
 * the way the quick switcher's provider does.
 *
 * `Enter` is deliberately not here. The registry's listener sits on the document and prevents the
 * default of everything it answers, which on the active row would kill every button inside it; the
 * list handles `Enter` on the row itself instead (`WindowedList`'s `onActivateRow`), and the entry
 * below documents both spellings in one label. `j` and `k` are in the registry for a different
 * reason: the roving walk itself belongs to the list, which is where the rows are, so the binding's
 * own job is to put the focus *into* the timeline — every further press is then the list's own.
 */

import type { ShortcutDef } from "../../core/modules/registry.ts"
import { setPendingSearch } from "../../core/search/index.ts"
import type { ConversationId } from "../../core/store/model.ts"
import { Shared, useShared } from "../../core/store/shared.ts"
import { client } from "../../core/store/store.ts"
import { openJumpToDate } from "./list/JumpToDate.tsx"
import { conversationPath, dmPath, navigateTo } from "./navigation.ts"
import { requestBrowseChannels } from "./sidebar/browseChannels.ts"
import { nextConversation } from "./state/selectors.ts"
import type { ConversationView } from "./wire/views.ts"

/** What the open conversation lends its shortcuts. */
export interface ConversationController {
	/** Which conversation this controller speaks for; `Mod+F` searches inside it. */
	conv: ConversationId
	/** Opens the thread of the row the roving tabindex is on (`t`, `r`). */
	openThreadOfActiveRow(): void
	/** Reacts to that row with the emoji this person used last (`e`), or takes that back. */
	reactToActiveRow(): void
	/** Puts the focus on the roving row, which is where `j` and `k` carry on from. */
	focusActiveRow(): void
	focusComposer(): void
	/** `Escape`: the one door between the message box and the timeline, in both directions. */
	moveBetweenBoxAndRows(): void
	jumpToFirstUnread(): void
	markAllRead(): void
	/** Toggles the conversation's own details view (`Mod+.`). */
	toggleDetails(): void
}

let controller: ConversationController | null = null

/** Installed by `ConversationView` while it is mounted; cleared when it is not. */
export function setConversationController(next: ConversationController | null): void {
	controller = next
}

/*
 * The "new message" dialog is mounted by the sidebar's direct-message section, which is what is on
 * screen wherever that section is — so `Mod+N` asks for it the way Home's *Browse channels* action
 * asks for the browse dialog (`sidebar/browseChannels.ts`). The flag lives out here because a
 * binding has no component of its own to hold state in.
 */
const newMessageWanted = new Shared(false)

/** Opens the "new message" dialog from anywhere in the application; `Mod+N` is its one caller. */
export function requestNewMessage(): void {
	newMessageWanted.set(true)
}

/** The dialog's open state, for the one component that mounts it. */
export function useNewMessageRequest(): { open: boolean; setOpen: (next: boolean) => void } {
	return {
		open: useShared(newMessageWanted),
		setOpen: newMessageWanted.set,
	}
}

/** Where a conversation is read: a channel at its slug, a direct one at its handle. */
function pathOf(meta: ConversationView): string {
	const direct = meta.kind === "dm" || meta.kind === "groupDm"
	return direct ? dmPath(meta.dmHandle ?? "") : conversationPath(meta.slug ?? "")
}

function metaOf(conv: ConversationId | undefined): ConversationView | undefined {
	if (conv === undefined) return undefined
	return client.api.getState().conversations[conv]?.meta as ConversationView | undefined
}

/**
 * One step through the order the sidebar itself is in (`nextConversation` over `sidebarOrder`): what
 * these keys walk is what the rows show, which is the only way the movement can be predicted from
 * the screen. `unreadOnly` is the Shift variant — the next conversation with something in it, which
 * is how a morning is worked through.
 *
 * With no conversation open the walk starts at the end it is coming from, so `Alt+↓` from Activity
 * opens the first row rather than nothing.
 */
function step(dir: 1 | -1, unreadOnly: boolean): void {
	const model = client.api.getState()
	const next = nextConversation(model, controller?.conv, dir, unreadOnly)
	const meta = metaOf(next)
	if (meta === undefined) return
	navigateTo(pathOf(meta))
}

/**
 * `Mod+F`: the search screen, prepared to search inside this conversation. The term itself never
 * travels in a URL (G-55) — `core/search/searchState.ts` is the hand-over between the two screens —
 * and what is handed over is the filter plus the space the reader's own word goes after. A direct
 * conversation has no slug to filter by, so it hands over nothing and the box is simply open.
 */
function searchThisConversation(): void {
	const slug = metaOf(controller?.conv)?.slug
	setPendingSearch(slug === undefined ? "" : `in:#${slug} `)
	navigateTo("/search")
}

export const chatShortcuts: ShortcutDef[] = [
	{
		id: "chat.openThread",
		// `t` is the primary; `r` is what half the world types for "reply" (X-A4 N16).
		keys: ["t", "r"],
		scope: "messageList",
		labelKey: "chat.shortcut.openThread",
		run: () => controller?.openThreadOfActiveRow(),
	},
	{
		id: "chat.reactLast",
		keys: ["e"],
		scope: "messageList",
		labelKey: "chat.shortcut.reactLast",
		run: () => controller?.reactToActiveRow(),
	},
	{
		id: "chat.walkMessages",
		keys: ["j", "k"],
		scope: "messageList",
		labelKey: "chat.shortcut.walkMessages",
		run: () => controller?.focusActiveRow(),
	},
	{
		// The one door, both ways: from a row into the box, and from the box back onto the row the
		// reader left. Without the second half the only way from the caret to the timeline is Shift+Tab
		// through the formatting row, and `j`/`k` — typed text inside the box — cannot be it.
		id: "chat.focusComposer",
		keys: ["Escape"],
		scope: "messageList",
		labelKey: "chat.shortcut.focusComposer",
		run: () => controller?.moveBetweenBoxAndRows(),
	},
	{
		id: "chat.jumpFirstUnread",
		keys: ["Ctrl+J"],
		scope: "conversation",
		labelKey: "chat.shortcut.jumpFirstUnread",
		run: () => controller?.jumpToFirstUnread(),
	},
	{
		// The sibling of the key above: `Ctrl+J` goes to the first thing unread, this one to a day.
		// `Alt+J` carries it on Windows and Linux, where Chrome and Firefox take `Ctrl+Shift+J` for
		// their own console before the page is told a key was pressed at all — `Mod+Shift+J` is the
		// documented chord and fires as written wherever the browser leaves it alone (X-A4 N12).
		id: "chat.jumpToDate",
		keys: ["Mod+Shift+J", "Alt+J"],
		scope: "conversation",
		labelKey: "chat.shortcut.jumpToDate",
		run: openJumpToDate,
	},
	{
		id: "chat.markAllRead",
		keys: ["Shift+Escape"],
		scope: "conversation",
		labelKey: "chat.shortcut.markAllRead",
		run: () => controller?.markAllRead(),
	},
	{
		// Slack's key for the same thing. Ctrl+Shift+I, which some clients use, is the browser's
		// developer tools and cannot be taken.
		id: "chat.details",
		keys: ["Mod+."],
		scope: "conversation",
		labelKey: "chat.shortcut.details",
		run: () => controller?.toggleDetails(),
	},
	{
		id: "chat.searchConversation",
		keys: ["Mod+F"],
		scope: "conversation",
		labelKey: "chat.shortcut.searchConversation",
		run: searchThisConversation,
	},
	{
		id: "chat.nextConversation",
		keys: ["Alt+ArrowDown"],
		scope: "global",
		labelKey: "chat.shortcut.nextConversation",
		run: () => step(1, false),
	},
	{
		id: "chat.previousConversation",
		keys: ["Alt+ArrowUp"],
		scope: "global",
		labelKey: "chat.shortcut.previousConversation",
		run: () => step(-1, false),
	},
	{
		id: "chat.nextUnread",
		keys: ["Alt+Shift+ArrowDown"],
		scope: "global",
		labelKey: "chat.shortcut.nextUnread",
		run: () => step(1, true),
	},
	{
		id: "chat.previousUnread",
		keys: ["Alt+Shift+ArrowUp"],
		scope: "global",
		labelKey: "chat.shortcut.previousUnread",
		run: () => step(-1, true),
	},
	{
		id: "chat.browseChannels",
		keys: ["Mod+Shift+L"],
		scope: "global",
		labelKey: "chat.shortcut.browseChannels",
		run: requestBrowseChannels,
	},
	{
		id: "chat.newMessage",
		keys: ["Mod+N"],
		scope: "global",
		labelKey: "chat.shortcut.newMessage",
		run: requestNewMessage,
	},
]
